import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FilterResponse } from "../models/filter";
import { getFilters } from "../api/filters";
import useUserContext from "../components/Authorization/useUserContext";
import { UserState } from "../components/Authorization/AuthorizationState";
import { isTokenExpired } from "../utils/auth/isTokenExpired";

// Filter facets rarely change at runtime, so cache indefinitely and never refetch.
const filterOptionsQuery = (user: UserState) => ({
  queryKey: ["filters", user.selectedRoles],
  queryFn: (): Promise<FilterResponse> => getFilters(user),
  staleTime: Infinity,
  cacheTime: Infinity,
});

export default function useFilterOptions() {
  const user = useUserContext();

  return useQuery(filterOptionsQuery(user));
}

/**
 * Fetches the filter facets once on authenticated app load and primes them into
 * the react-query cache, so opening filters / navigating to Explore resolves
 * from cache instead of firing a cold request. Uses the same query key/fetcher
 * as useFilterOptions so consumers get a cache hit with no refetch.
 */
export function usePrefetchFilterOptions() {
  const queryClient = useQueryClient();
  const user = useUserContext();
  const hasPrefetched = useRef(false);

  useEffect(() => {
    if (hasPrefetched.current) return;
    if (user.isLoading || isTokenExpired(user.token)) return;

    hasPrefetched.current = true;
    queryClient.prefetchQuery(filterOptionsQuery(user));
  }, [user, queryClient]);
}
